import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Main.css';
import PhoneList from './products/PhoneList';
import InternetList from './products/InternetList';
import Chat from './Chat';
import ChatDetail from './ChatDetail';
import MyPage, { MyPageHandle, SubPageType } from './MyPage';
import PhoneConsult from './PhoneConsult';
import Notification from './Notification';
import Popup from '../components/common/Popup';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

import imgLogo from '../assets/images/logo-main.svg';
import imgPhone from '../assets/images/phone-main.png';
import imgIconBack from '../assets/icon-back-2.svg';
import iconTvAdd from '../assets/images/icon_tv_add.svg';
import iconMobile from '../assets/images/icon_mobile.svg';
import { requestFCMToken, onForegroundMessage } from '../firebase';

type ProductType = 'internet' | 'phoneProduct' | null;

interface Banner {
  id: string;
  title: string;
  description: string;
  image_url: string;
  link_url: string;
  display_order: number;
}

interface MainProps {
  shouldShowPopup: boolean;
  setShouldShowPopup: (show: boolean) => void;
}

const Main: React.FC<MainProps> = ({ shouldShowPopup, setShouldShowPopup }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, updateFcmToken } = useAuth();

  // URL 경로에서 현재 상태 파싱
  const routeInfo = useMemo(() => {
    const path = location.pathname.replace('/main', '') || '/';
    const segments = path.split('/').filter(Boolean);

    // 기본값
    let tab: 'home' | 'chat' | 'phone' | 'mypage' = 'home';
    let product: ProductType = null;
    let chatRoomId: string | null = null;
    let showNotification = false;
    let myPageSubPage: SubPageType = 'main';
    let isMyPageSubPage = false;

    if (segments.length === 0 || segments[0] === 'home') {
      tab = 'home';
    } else if (segments[0] === 'products') {
      tab = 'home';
      if (segments[1] === 'internet') product = 'internet';
      else if (segments[1] === 'phone') product = 'phoneProduct';
    } else if (segments[0] === 'chat') {
      tab = 'chat';
      if (segments[1]) chatRoomId = segments[1];
    } else if (segments[0] === 'phone') {
      tab = 'phone';
    } else if (segments[0] === 'mypage') {
      tab = 'mypage';
      if (segments[1]) {
        myPageSubPage = segments[1] as SubPageType;
        isMyPageSubPage = true;
      }
    } else if (segments[0] === 'notifications') {
      showNotification = true;
    }

    console.log('📍 Route parsed:', { path, segments, tab, chatRoomId });
    return { tab, product, chatRoomId, showNotification, myPageSubPage, isMyPageSubPage };
  }, [location.pathname]);

  // URL 기반 상태
  const activeTab = routeInfo.tab;
  const activeProduct = routeInfo.product;
  const activeChatRoomId = routeInfo.chatRoomId;
  const showNotification = routeInfo.showNotification;
  const myPageInitialPage = routeInfo.myPageSubPage;
  const isMyPageSubPage = routeInfo.isMyPageSubPage;

  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [bannersLoading, setBannersLoading] = useState(true);

  // ref for child components
  const myPageRef = useRef<MyPageHandle>(null);

  // 알림 데이터 (읽지 않은 알림 개수)
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  // 읽지 않은 알림 개수 조회
  useEffect(() => {
    if (!customer) return;

    const loadUnreadCount = async () => {
      try {
        const { count, error } = await supabase
          .from('notification_reads')
          .select('*', { count: 'exact', head: true })
          .eq('customer_id', customer.id)
          .eq('is_read', false);

        if (error) {
          console.error('읽지 않은 알림 조회 실패:', error);
          return;
        }

        setUnreadNotificationCount(count || 0);
      } catch (error) {
        console.error('읽지 않은 알림 조회 오류:', error);
      }
    };

    loadUnreadCount();

    // 실시간 업데이트 구독 - notification_reads만 감지
    console.log('Realtime 구독 시작 - customer_id:', customer.id);

    const channel = supabase
      .channel('notification_reads_main_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notification_reads',
          filter: `customer_id=eq.${customer.id}`
        },
        (payload) => {
          console.log('✅ notification_reads 변경 감지:', payload);
          loadUnreadCount();
        }
      )
      .subscribe((status, err) => {
        console.log('📡 Realtime 구독 상태:', status);
        if (err) {
          console.error('❌ Realtime 구독 에러:', err);
        }
        if (status === 'SUBSCRIBED') {
          console.log('✅ Realtime 구독 성공!');
        }
        if (status === 'CHANNEL_ERROR') {
          console.error('❌ 채널 에러 발생');
        }
        if (status === 'TIMED_OUT') {
          console.error('❌ 구독 타임아웃');
        }
      });

    return () => {
      console.log('🔌 Realtime 구독 해제');
      supabase.removeChannel(channel);
    };
  }, [customer]);

  // 알림 화면을 닫을 때 카운트 업데이트
  useEffect(() => {
    if (!showNotification && customer) {
      // 알림 화면에서 돌아왔을 때 카운트 다시 로드
      const loadUnreadCount = async () => {
        try {
          const { count, error } = await supabase
            .from('notification_reads')
            .select('*', { count: 'exact', head: true })
            .eq('customer_id', customer.id)
            .eq('is_read', false);

          if (error) {
            console.error('읽지 않은 알림 조회 실패:', error);
            return;
          }

          setUnreadNotificationCount(count || 0);
        } catch (error) {
          console.error('읽지 않은 알림 조회 오류:', error);
        }
      };

      loadUnreadCount();
    }
  }, [showNotification, customer]);

  // Supabase에서 배너 데이터 가져오기
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setBannersLoading(true);
        const { data, error } = await supabase
          .from('banners')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        if (error) {
          console.error('배너 조회 실패:', error);
          return;
        }

        setBanners(data || []);
      } catch (error) {
        console.error('배너 조회 오류:', error);
      } finally {
        setBannersLoading(false);
      }
    };

    fetchBanners();
  }, []);

  // 자동 슬라이드 (3초마다)
  useEffect(() => {
    if (banners.length === 0) return;

    const timer = setInterval(() => {
      setCurrentBannerIndex((prevIndex) => (prevIndex + 1) % banners.length);
    }, 3000);

    return () => clearInterval(timer);
  }, [banners.length]);

  // 배너 클릭 핸들러
  const handleBannerClick = (linkUrl: string) => {
    window.open(linkUrl, '_blank');
  };

  // FCM 토큰 처리 (앱/웹 통합)
  useEffect(() => {
    console.log('🔔 FCM useEffect 실행됨, customer:', customer?.id);

    if (!customer) {
      console.log('⚠️ customer가 없어서 FCM 토큰 요청 스킵');
      return;
    }

    let fcmTokenRequested = false; // 중복 요청 방지

    const handleMessage = (event: MessageEvent) => {
      // Flutter WebView에서 보낸 메시지 처리
      if (event.data && event.data.type === 'FCM_TOKEN') {
        const { token, platform } = event.data;

        if (customer && token && (platform === 'ios' || platform === 'android')) {
          updateFcmToken(token, platform);
        }
      }
    };

    // Flutter에서 보내는 flutterFcmToken 커스텀 이벤트 처리
    const handleFlutterFcmToken = (event: CustomEvent) => {
      const { token, platform } = event.detail;
      console.log('📱 Flutter FCM 토큰 이벤트 수신:', platform);
      if (customer && token && (platform === 'ios' || platform === 'android')) {
        updateFcmToken(token, platform);
      }
    };

    // 메시지 리스너 등록
    window.addEventListener('message', handleMessage);
    window.addEventListener('flutterFcmToken', handleFlutterFcmToken as EventListener);

    // Flutter WebView 환경 감지 (localStorage 우선, User-Agent 보조)
    const flutterPlatform = localStorage.getItem('flutter_device_platform') as 'ios' | 'android' | null;
    const flutterFcmToken = localStorage.getItem('flutter_fcm_token');
    const ua = navigator.userAgent.toLowerCase();
    const isFlutterWebView = !!flutterPlatform || ua.includes('wv') || ua.includes('webview');

    console.log('🌐 환경 체크 - isFlutterWebView:', isFlutterWebView, 'platform:', flutterPlatform);

    if (isFlutterWebView) {
      // Flutter WebView 환경
      if (flutterFcmToken && flutterPlatform) {
        // Flutter가 제공한 토큰 사용
        console.log('📱 Flutter FCM 토큰 사용:', flutterPlatform);
        updateFcmToken(flutterFcmToken, flutterPlatform);
      } else {
        // 토큰이 아직 없으면 Flutter 이벤트 대기
        console.log('📱 Flutter WebView 환경 - Flutter FCM 토큰 대기 중');
      }
    } else if (!fcmTokenRequested) {
      // 웹 브라우저 환경 - 웹 FCM 토큰 요청 (한 번만)
      fcmTokenRequested = true;
      console.log('💻 웹 브라우저 환경 - 웹 FCM 토큰 요청 시작');

      requestFCMToken().then(token => {
        if (token) {
          console.log('✅ 웹 FCM 토큰 획득:', token);
          updateFcmToken(token, 'web');
        } else {
          console.log('⚠️ 웹 FCM 토큰이 null로 반환됨');
        }
      }).catch(error => {
        console.error('❌ 웹 FCM 토큰 요청 실패:', error);
      });

      // 포그라운드 메시지 수신 핸들러
      console.log('🔔 Main.tsx: onForegroundMessage 핸들러 설정 시작');
      onForegroundMessage((payload) => {
        console.log('📩 Main.tsx: 포그라운드 알림 수신:', payload);

        // 브라우저 알림 표시
        const title = payload.notification?.title || '새 알림';
        const options = {
          body: payload.notification?.body || '',
          icon: payload.notification?.icon || '/logo-main.svg',
          badge: '/logo-main.svg',
          tag: payload.data?.notification_id || 'notification',
          requireInteraction: false,
          data: payload.data
        };

        // 알림 표시
        if (window.Notification.permission === 'granted') {
          new window.Notification(title, options);
        }

        // 알림 카운트 다시 로드
        const loadUnreadCount = async () => {
          try {
            const { count, error } = await supabase
              .from('notification_reads')
              .select('*', { count: 'exact', head: true })
              .eq('customer_id', customer.id)
              .eq('is_read', false);

            if (!error) {
              setUnreadNotificationCount(count || 0);
            }
          } catch (error) {
            console.error('읽지 않은 알림 조회 오류:', error);
          }
        };
        loadUnreadCount();
      });
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('flutterFcmToken', handleFlutterFcmToken as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]); // customer.id만 의존성으로 설정

  // 팝업 표시 처리
  useEffect(() => {
    if (shouldShowPopup && activeTab === 'home') {
      setShowPopup(true);
    }
  }, [shouldShowPopup, activeTab]);

  // 팝업 닫기
  const handleClosePopup = () => {
    setShowPopup(false);
    setShouldShowPopup(false);
  };

  // 오늘 하루 보지 않기
  const handleDoNotShowToday = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0); // 다음 날 자정
    localStorage.setItem('popup_hide_until', tomorrow.getTime().toString());
    handleClosePopup();
  };

  // 뒤로가기 버튼을 보여줄지 결정
  const shouldShowBackButton = showNotification || activeProduct !== null || activeChatRoomId !== null || isMyPageSubPage;

  // 뒤로가기 핸들러 - URL 기반
  const handleBack = () => {
    navigate(-1); // 브라우저 히스토리 뒤로가기
  };

  return (
    <div className="main-container">
      {/* Header */}
      <header className="main-header">
        {/* 모바일 뒤로가기 버튼 */}
        {shouldShowBackButton && (
          <button className="mobile-back-btn" onClick={handleBack}>
            <img src={imgIconBack} alt="뒤로가기" />
          </button>
        )}
        <div className="logo-main-wrapper">
          <img src={imgLogo} alt="삐삐 로고" className="main-logo" />
        </div>
        <button className="mobile-notification-btn" onClick={() => navigate('/main/notifications')}>
          <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
            <path d="M14 23.3333C15.1046 23.3333 16 22.4379 16 21.3333H12C12 22.4379 12.8954 23.3333 14 23.3333Z" fill="white"/>
            <path d="M21 16.3333V11.3333C21 7.94167 19.0867 5.14167 16 4.37V3.66667C16 2.56667 15.1 1.66667 14 1.66667C12.9 1.66667 12 2.56667 12 3.66667V4.37C8.91333 5.14167 7 7.94167 7 11.3333V16.3333L5 18.3333V19.3333H23V18.3333L21 16.3333Z" fill="white"/>
          </svg>
          {unreadNotificationCount > 0 && (
            <span className="notification-badge"></span>
          )}
        </button>
        <nav className="main-nav">
          <button
            className={`nav-link ${activeTab === 'home' && !activeProduct ? 'active' : ''}`}
            onClick={() => navigate('/main')}
          >
            홈
          </button>
          <button
            className={`nav-link ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => navigate('/main/chat')}
          >
            채팅
          </button>
          <button
            className={`nav-link ${activeTab === 'phone' ? 'active' : ''}`}
            onClick={() => navigate('/main/phone')}
          >
            전화상담
          </button>
          <button
            className={`nav-link ${activeTab === 'mypage' ? 'active' : ''}`}
            onClick={() => navigate('/main/mypage')}
          >
            마이페이지
          </button>
          <button className="notification-btn" onClick={() => navigate('/main/notifications')}>
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path d="M14 23.3333C15.1046 23.3333 16 22.4379 16 21.3333H12C12 22.4379 12.8954 23.3333 14 23.3333Z" fill="white"/>
              <path d="M21 16.3333V11.3333C21 7.94167 19.0867 5.14167 16 4.37V3.66667C16 2.56667 15.1 1.66667 14 1.66667C12.9 1.66667 12 2.56667 12 3.66667V4.37C8.91333 5.14167 7 7.94167 7 11.3333V16.3333L5 18.3333V19.3333H23V18.3333L21 16.3333Z" fill="white"/>
            </svg>
            {unreadNotificationCount > 0 && (
              <span className="notification-badge"></span>
            )}
          </button>
        </nav>
      </header>

      {/* Main Content */}
      <div className={`main-content-area${(activeChatRoomId !== null || activeProduct !== null || showNotification || isMyPageSubPage) ? ' hide-bottom-nav' : ''}`}>
        {showNotification && <Notification onBack={() => navigate(-1)} />}
        {!showNotification && activeProduct === 'internet' && (
          <InternetList
            onBack={() => navigate(-1)}
            onSwitchToPhone={() => navigate('/main/products/phone', { replace: true })}
          />
        )}
        {!showNotification && activeProduct === 'phoneProduct' && (
          <PhoneList
            onBack={() => navigate(-1)}
            onSwitchToInternet={() => navigate('/main/products/internet', { replace: true })}
          />
        )}
        {!showNotification && activeProduct === null && activeTab === 'chat' && activeChatRoomId === null && <Chat onChatRoomClick={(roomId) => navigate(`/main/chat/${roomId}`)} />}
        {!showNotification && activeProduct === null && activeTab === 'chat' && activeChatRoomId !== null && <ChatDetail roomId={activeChatRoomId} onBack={() => navigate(-1)} />}
        {!showNotification && activeProduct === null && activeTab === 'mypage' && <MyPage ref={myPageRef} initialPage={myPageInitialPage} />}
        {!showNotification && activeProduct === null && activeTab === 'phone' && <PhoneConsult />}
        {!showNotification && activeProduct === null && activeTab === 'home' && (
        <div className="main-content-inner">
          {/* Welcome Section */}
          <div className="welcome-section">
            <div className="welcome-text-area">
              <p className="welcome-greeting">{customer?.nickname || '게스트'} 님, 안녕하세요!</p>
              <div className="welcome-main-text">
                <p>간편 연락,</p>
                <p>
                  재가입 일정 관리 !<br />
                  <span className="brand-name">BBIBBI</span>에서 함께해요 !
                </p>
              </div>
            </div>
            <div className="welcome-image-area">
              <img src={imgPhone} alt="삐삐" className="welcome-phone-img" />
            </div>
          </div>

          {/* Service Buttons */}
          <div className="service-buttons-row">
            <button className="service-card" onClick={() => navigate('/main/products/internet')}>
              <img src={iconTvAdd} alt="인터넷/TV" className="service-icon" />
              <span>인터넷/TV</span>
            </button>
            <button className="service-card" onClick={() => navigate('/main/products/phone')}>
              <img src={iconMobile} alt="핸드폰/워치" className="service-icon" />
              <span>핸드폰/워치</span>
            </button>
          </div>

          {/* Special Offer Section */}
          <div className="special-offer-section">
            <div className="offer-header-row">
              <div className="offer-text-group">
                <h3 className="offer-title">오늘의 삐삐 단독 특가</h3>
                <p className="offer-subtitle">삐삐에서만 볼 수 있는 특별한 가격</p>
              </div>
              {banners.length > 0 && (
                <button className="arrow-icon-btn" onClick={() => handleBannerClick(banners[currentBannerIndex].link_url)}>
                  <img src={imgIconBack} alt="화살표" className="arrow-rotated" />
                </button>
              )}
            </div>
            {bannersLoading ? (
              <div className="offer-banner-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
                <p style={{ color: '#999' }}>배너를 불러오는 중...</p>
              </div>
            ) : banners.length > 0 ? (
              <div
                className="offer-banner-card"
                onClick={() => handleBannerClick(banners[currentBannerIndex].link_url)}
                style={{ cursor: 'pointer' }}
              >
                <div className="banner-text-content">
                  <h4>{banners[currentBannerIndex].title}</h4>
                  <p>{banners[currentBannerIndex].description}</p>
                </div>
                <div className="banner-img-wrapper">
                  <img src={banners[currentBannerIndex].image_url} alt="배너" className="banner-img" />
                </div>
                <div className="banner-pagination">{currentBannerIndex + 1} / {banners.length}</div>
              </div>
            ) : (
              <div className="offer-banner-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
                <p style={{ color: '#999' }}>표시할 배너가 없습니다</p>
              </div>
            )}

            {/* 배너 인디케이터 (점) */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              marginTop: '12px'
            }}>
              {banners.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentBannerIndex(index)}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: currentBannerIndex === index ? '#eee0af' : '#555555',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'all 0.3s'
                  }}
                  aria-label={`배너 ${index + 1}`}
                />
              ))}
            </div>
          </div>

        </div>
        )}

      </div>

      {/* Footer - 웹에서만 표시 (메인 탭에서만, 서브페이지에서는 숨김) */}
      {!showNotification && activeProduct === null && activeChatRoomId === null && !isMyPageSubPage && (
        <Footer
          onTermsClick={() => navigate('/main/mypage/terms')}
          onPrivacyClick={() => navigate('/main/mypage/privacy')}
        />
      )}

      {/* 모바일 하단 네비게이션 - 서브 페이지에서는 숨김 */}
      {activeChatRoomId === null && activeProduct === null && !showNotification && !isMyPageSubPage && (
      <nav className="mobile-bottom-nav">
        <button
          className={`mobile-nav-btn ${activeTab === 'home' && !activeProduct ? 'active' : ''}`}
          onClick={() => navigate('/main')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="currentColor"/>
          </svg>
          <span>홈</span>
        </button>
        <button
          className={`mobile-nav-btn ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => navigate('/main/chat')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" fill="currentColor"/>
          </svg>
          <span>채팅</span>
        </button>
        <button
          className={`mobile-nav-btn ${activeTab === 'phone' ? 'active' : ''}`}
          onClick={() => navigate('/main/phone')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" fill="currentColor"/>
          </svg>
          <span>전화상담</span>
        </button>
        <button
          className={`mobile-nav-btn ${activeTab === 'mypage' ? 'active' : ''}`}
          onClick={() => navigate('/main/mypage')}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
          </svg>
          <span>마이페이지</span>
        </button>
      </nav>
      )}

      {/* 팝업 */}
      {showPopup && (
        <Popup
          onClose={handleClosePopup}
          onDoNotShowToday={handleDoNotShowToday}
        />
      )}
    </div>
  );
};

export default Main;
